import { FC, useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Checkbox, FormControlLabel, MenuItem, Stack, TextField } from '@mui/material'

import { LanguageMaps } from '../../../../api/language'
import { InputTranslator } from '../../../../components/InputTranslator/InputTranslator'
import { InternalFormDialog } from '../../../../components/InternalFormDialog/InternalFormDialog'
import { InternalImageUpload } from '../../../../components/InternalImageUpload/InternalImageUpload'
import { InternalConcept, InternalTag, InternalTagSaveBody, InternalTagType } from '../../../../types/InternalTag'
import {
  getConceptForTag,
  getFeatureTags,
  tagLanguages,
  TagFormValues,
  tagTypeLabelKeys,
  tagTypes,
  toTagFormValues,
  toTagSaveBody,
} from '../helpers/tagHelpers'

type TagEditDialogProps = {
  open: boolean
  tag?: InternalTag
  defaultType: InternalTagType
  tags: InternalTag[]
  concepts?: InternalConcept[]
  languageMaps?: LanguageMaps
  isSaving?: boolean
  onClose: () => void
  onSave: (body: InternalTagSaveBody) => void
}

// No <form> here: the InputTranslator dialog has its own form, and React bubbles its submit through the portal into any parent form.
export const TagEditDialog: FC<TagEditDialogProps> = ({ open, tag, defaultType, tags, concepts, languageMaps, isSaving, onClose, onSave }) => {
  const { t } = useTranslation()
  const concept = useMemo(() => getConceptForTag(concepts, tag?.id), [concepts, tag?.id])
  const { control, handleSubmit, reset, formState, watch } = useForm<TagFormValues>({
    mode: 'onChange',
    defaultValues: toTagFormValues(tag, languageMaps, concept, defaultType),
  })

  useEffect(() => {
    if (open) {
      reset(toTagFormValues(tag, languageMaps, concept, defaultType))
    }
    // The form is only reset when the dialog opens, so a refetch while editing doesn't drop the edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tag, defaultType, reset])

  const type = watch('type')
  const imageUrl = watch('imageUrl')
  const featureTags = useMemo(() => getFeatureTags(tags), [tags])
  const targetCount = tag?.targets?.length ?? 0
  const typeChanged = !!tag && type !== tag.type

  const submit = handleSubmit((values) => onSave(toTagSaveBody(tag, values, concept)))

  return (
    <InternalFormDialog
      open={open}
      title={tag ? t('internal-service-management:tags_edit') : t('internal-service-management:tags_new')}
      onClose={onClose}
      onSave={submit}
      saveDisabled={isSaving || !formState.isValid}
    >
      <Stack spacing={2} pt={1}>
        <Controller
          name="names"
          control={control}
          rules={{ validate: (names) => !!names.en?.trim() }}
          render={({ field }) => (
            <InputTranslator
              value={field.value}
              onChange={field.onChange}
              label={t('internal-service-management:tags_name')}
              dialogTitle={t('internal-service-management:tags_name')}
              selectedLanguages={tagLanguages}
            />
          )}
        />
        <Controller
          name="descriptions"
          control={control}
          render={({ field }) => (
            <InputTranslator
              value={field.value}
              onChange={field.onChange}
              label={t('internal-service-management:tags_description')}
              dialogTitle={t('internal-service-management:tags_description')}
              selectedLanguages={tagLanguages}
              multiline
              maxRows={4}
            />
          )}
        />
        <Controller
          name="imageUrl"
          control={control}
          render={({ field }) => (
            <InternalImageUpload
              value={field.value}
              onChange={field.onChange}
              type="tagImage"
              label={t('internal-service-management:tags_image')}
              previewVariant="contain"
              previewSize={120}
              removable
            />
          )}
        />
        {imageUrl && (
          <Controller
            name="imageTexts"
            control={control}
            render={({ field }) => (
              <InputTranslator
                value={field.value}
                onChange={field.onChange}
                label={t('internal-service-management:tags_image_text')}
                dialogTitle={t('internal-service-management:tags_image_text')}
                selectedLanguages={tagLanguages}
                multiline
                maxRows={4}
              />
            )}
          />
        )}
        <Controller
          name="type"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              select
              size="small"
              label={t('internal-service-management:tags_type')}
              helperText={typeChanged && targetCount > 0 ? t('internal-service-management:tags_type_change_clears_targets', { count: targetCount }) : undefined}
            >
              {tagTypes.map((tagType) => (
                <MenuItem key={tagType} value={tagType}>
                  {t(tagTypeLabelKeys[tagType])}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
        {type === InternalTagType.Concept && (
          <Controller
            name="featureTagId"
            control={control}
            render={({ field }) => (
              <TextField {...field} select size="small" label={t('internal-service-management:tags_feature_tag')}>
                <MenuItem value="">{t('internal-common:none')}</MenuItem>
                {featureTags.map((featureTag) => (
                  <MenuItem key={featureTag.id} value={featureTag.id}>
                    {featureTag.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        )}
        <Controller
          name="active"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={<Checkbox checked={field.value} onChange={(event) => field.onChange(event.target.checked)} />}
              label={t('internal-service-management:tags_active')}
            />
          )}
        />
      </Stack>
    </InternalFormDialog>
  )
}
